import { useCallback, useEffect, useRef, useState } from 'react'
import {
    ChevronLeft,
    ChevronRight,
    Download,
    Eye,
    Trash2,
    Upload,
} from 'lucide-react'
import type { DraftItem } from './use-attachment-draft'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { formatBytes } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

const DEFAULT_ACCEPT =
    'image/*,.pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,' +
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
    'application/vnd.ms-excel,' +
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

function badgeFor(name: string): { label: string; cls: string } {
    const ext = name.split('.').pop()?.toLowerCase() ?? ''
    if (ext === 'pdf') return { label: 'PDF', cls: 'bg-red-500' }
    if (ext === 'doc' || ext === 'docx')
        return { label: 'DOC', cls: 'bg-blue-600' }
    if (ext === 'xls' || ext === 'xlsx')
        return { label: 'XLS', cls: 'bg-green-600' }
    return {
        label: ext.toUpperCase().slice(0, 4) || 'FILE',
        cls: 'bg-muted-foreground',
    }
}

/**
 * Phần của draft mà field thực sự đụng tới. Khai riêng (thay vì buộc phải là
 * `AttachmentDraft` của hộ trồng) để màn khác cấp draft của mình — vd ảnh bill
 * chuyển khoản chỉ có 1 ảnh và commit ra file id. `AttachmentDraft` vẫn thoả
 * kiểu này nên form hộ trồng không phải sửa gì.
 */
export type AttachmentsFieldDraft = {
    items: DraftItem[]
    addFiles: (files: FileList | File[]) => void
    remove: (item: DraftItem) => void
}

type Props = {
    draft: AttachmentsFieldDraft
    readOnly?: boolean
    /** Mặc định: ảnh + tài liệu văn phòng. Truyền 'image/*' để chỉ nhận ảnh. */
    accept?: string
    /** Mặc định true. false = chỉ giữ 1 tệp, có tệp rồi thì ẩn ô thêm. */
    multiple?: boolean
    /** Dòng gợi ý trên ô thêm; mặc định mô tả các loại tệp của hồ sơ hộ. */
    typesHint?: string
    /**
     * Tên của chính ô đính kèm này (vd "Giấy tờ vùng trồng"). Màn hồ sơ hộ có
     * hai ô cạnh nhau, cả hai nút đều đọc là "Thêm tệp" nên người dùng bàn phím
     * / trình đọc màn hình không biết đang ở ô nào. Bỏ trống thì giữ nguyên tên
     * cũ — call-site chỉ có một ô không phải sửa gì.
     */
    label?: string
    /** Câu hiện khi chỉ xem mà chưa có tệp; mặc định câu của hồ sơ hộ. */
    emptyText?: string
}

/** Giấy tờ đính kèm: dropzone + lưới thẻ file, preview ảnh trong modal. */
export function GrowerAttachmentsField({
    draft,
    readOnly,
    accept,
    multiple = true,
    typesHint,
    label,
    emptyText,
}: Props) {
    const inputRef = useRef<HTMLInputElement>(null)
    // Preview chỉ dành cho ảnh, có thể prev/next qua các ảnh.
    const imageItems = draft.items.filter((i) => i.isImage)
    const [previewIdx, setPreviewIdx] = useState<number | null>(null)
    const preview = previewIdx != null ? (imageItems[previewIdx] ?? null) : null

    const openPreview = useCallback(
        (item: DraftItem) => {
            const i = imageItems.findIndex((x) => x.key === item.key)
            setPreviewIdx(i >= 0 ? i : null)
        },
        [imageItems],
    )
    const step = useCallback(
        (delta: number) => {
            setPreviewIdx((cur) => {
                if (cur == null || imageItems.length === 0) return cur
                return (cur + delta + imageItems.length) % imageItems.length
            })
        },
        [imageItems.length],
    )

    useEffect(() => {
        if (previewIdx == null) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') step(-1)
            else if (e.key === 'ArrowRight') step(1)
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [previewIdx, step])

    return (
        <div className="space-y-3">
            {/* Ô chọn tệp ẩn, chỉ để nút "Thêm tệp" mở hộp thoại. Màn chỉ
                xem không có nút đó nên cũng không dựng ô nhập nào. */}
            {!readOnly && (
                <input
                    ref={inputRef}
                    type="file"
                    multiple={multiple}
                    accept={accept ?? DEFAULT_ACCEPT}
                    className="hidden"
                    data-testid="grower-attach-input"
                    aria-label={label}
                    onChange={(e) => {
                        if (e.target.files?.length)
                            draft.addFiles(e.target.files)
                        if (inputRef.current) inputRef.current.value = ''
                    }}
                />
            )}

            {readOnly && draft.items.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                    {emptyText ?? m.grower_attach_empty()}
                </p>
            ) : (
                <div className="grid grid-cols-[repeat(auto-fill,minmax(130px,1fr))] gap-3">
                    {draft.items.map((item) => {
                        const badge = badgeFor(item.name)
                        return (
                            <div
                                key={item.key}
                                data-testid="attach-card"
                                className="group relative overflow-hidden rounded-lg border bg-card"
                            >
                                {/* Máy cảm ứng không có hover: hiện sẵn nút xem/tải/xoá, nếu
                                    không người dùng điện thoại không xoá được tệp. */}
                                <div className="absolute right-1.5 top-1.5 z-10 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100">
                                    {item.isImage ? (
                                        <button
                                            type="button"
                                            title={m.action_view()}
                                            onClick={() => openPreview(item)}
                                            className="flex size-6 items-center justify-center rounded-md bg-white/90 text-foreground shadow-sm"
                                        >
                                            <Eye className="size-3.5" />
                                        </button>
                                    ) : (
                                        <a
                                            href={item.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            title={m.action_download()}
                                            className="flex size-6 items-center justify-center rounded-md bg-white/90 text-foreground shadow-sm"
                                        >
                                            <Download className="size-3.5" />
                                        </a>
                                    )}
                                    {!readOnly && (
                                        <button
                                            type="button"
                                            title={m.action_delete()}
                                            onClick={() => draft.remove(item)}
                                            className="flex size-6 items-center justify-center rounded-md bg-white/90 text-destructive shadow-sm"
                                        >
                                            <Trash2 className="size-3.5" />
                                        </button>
                                    )}
                                </div>

                                <div
                                    className="flex h-24 cursor-pointer items-center justify-center bg-muted"
                                    onClick={() =>
                                        item.isImage
                                            ? openPreview(item)
                                            : window.open(item.url, '_blank')
                                    }
                                >
                                    {item.isImage ? (
                                        <img
                                            src={item.url}
                                            alt={item.name}
                                            className="size-full object-cover"
                                        />
                                    ) : (
                                        <span
                                            className={`flex h-12 w-10 items-end justify-center rounded-md pb-1 text-[10px] font-extrabold text-white ${badge.cls}`}
                                        >
                                            {badge.label}
                                        </span>
                                    )}
                                </div>
                                <div className="px-2 py-1.5">
                                    <div
                                        className="truncate text-xs font-medium"
                                        title={item.name}
                                    >
                                        {item.name}
                                    </div>
                                    {item.size != null && (
                                        <div className="text-[11px] text-muted-foreground">
                                            {formatBytes(item.size)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                    {!readOnly && (multiple || draft.items.length === 0) && (
                        <button
                            type="button"
                            data-testid="grower-attach-add"
                            onClick={() => inputRef.current?.click()}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                                e.preventDefault()
                                if (e.dataTransfer.files.length)
                                    draft.addFiles(e.dataTransfer.files)
                            }}
                            className="flex min-h-[124px] cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed bg-muted/40 p-2 text-center hover:border-primary hover:bg-accent"
                            title={typesHint ?? m.grower_attach_types()}
                            aria-label={
                                label
                                    ? `${m.grower_attach_add()} — ${label}`
                                    : undefined
                            }
                        >
                            <Upload className="size-5 text-muted-foreground" />
                            <span className="text-xs font-medium">
                                {m.grower_attach_add()}
                            </span>
                        </button>
                    )}
                </div>
            )}

            <Dialog
                open={!!preview}
                onOpenChange={(o) => {
                    if (!o) setPreviewIdx(null)
                }}
            >
                <DialogContent className="max-w-3xl">
                    {preview && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between gap-3 pr-6">
                                <span className="truncate text-sm font-semibold">
                                    {preview.name}
                                    {imageItems.length > 1 && (
                                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                                            {(previewIdx ?? 0) + 1}/
                                            {imageItems.length}
                                        </span>
                                    )}
                                </span>
                                <a
                                    href={preview.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1.5 text-sm text-primary"
                                >
                                    <Download className="size-4" />{' '}
                                    {m.action_download()}
                                </a>
                            </div>
                            <div className="relative">
                                <img
                                    src={preview.url}
                                    alt={preview.name}
                                    className="mx-auto max-h-[70vh] w-auto rounded-md"
                                />
                                {imageItems.length > 1 && (
                                    <>
                                        <button
                                            type="button"
                                            aria-label={m.attach_prev_image()}
                                            onClick={() => step(-1)}
                                            className="absolute left-2 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70"
                                        >
                                            <ChevronLeft className="size-5" />
                                        </button>
                                        <button
                                            type="button"
                                            aria-label={m.attach_next_image()}
                                            onClick={() => step(1)}
                                            className="absolute right-2 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70"
                                        >
                                            <ChevronRight className="size-5" />
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    )
}
